import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import {
  deleteRouterForward,
  type RouterForwardDeleteResponse,
  type RouterProbeForward,
  type RouterProbeLanHost,
} from '@/api/zoneRouter';
import { extractApiError } from '@/utils/apiError';
import AddPortForwardDialog from './AddPortForwardDialog';
import { translatePortForwardError } from './portForwardErrors';
import { Tooltip } from '@/components/ui/tooltip';

interface Props {
  zoneId: number | string;
  forwards: RouterProbeForward[];
  /** LAN hosts from the latest probe — fed straight into the Add
   *  modal's device-picker dropdown (2026-06-02 redesign). Empty array
   *  when no probe has landed; the picker degrades to a free-text MAC
   *  input in that case. */
  lanHosts?: RouterProbeLanHost[];
  /** Default interface for the Add modal — Wireguard0 when WG is up,
   *  otherwise undefined (operator picks from the dropdown). */
  defaultInterface?: string;
  /** Fires on successful add OR delete so the parent re-fetches the
   *  probe; the table re-renders from the fresh `forwards` snapshot. */
  onChanged: () => void;
}

/**
 * Router-side port-forwards table (Phase C of the autodiscovery spec).
 *
 * Reads from the probe's `existing_forwards` snapshot — the source of
 * truth lives on the router itself, not in our DB. POST/DELETE go to
 * the BE write endpoints and the parent re-probes after a mutation,
 * which keeps "what's shown" honest about "what's actually on the
 * device" (the BE's readback assertion covers the partial-success
 * case).
 *
 * Visual polish pass (2026-06-02): proper bordered table with column
 * hierarchy + right-aligned action column; "Add forward" lives as an
 * outline button next to the section title; rows hover-react with the
 * same muted-bg treatment as the suggested-cameras list above.
 *
 * Spec: docs/superpowers/specs/2026-05-31-router-autodiscovery.md §C.
 */
export default function PortForwardsTable({
  zoneId,
  forwards,
  lanHosts = [],
  defaultInterface,
  onChanged,
}: Props) {
  const { t } = useT();
  const [addOpen, setAddOpen] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<RouterProbeForward | null>(null);

  // Stable key for React rows + deletion-in-flight lookups. Prefer
  // the device-side ``index`` (the BE delete addresses by it too); fall
  // back to a five-tuple-ish concat only as a row-key tiebreaker for
  // rows that somehow lack it. The actual DELETE call requires the
  // real index.
  const keyOf = (f: RouterProbeForward) =>
    f.index ||
    `${f.interface ?? ''}|${f.protocol ?? ''}|${f.external_port ?? ''}|${f.internal_port ?? ''}|${(f.to_host ?? '').toLowerCase()}`;

  const onDeleteConfirm = async () => {
    if (!confirmTarget) return;
    const fwd = confirmTarget;
    const key = keyOf(fwd);
    if (!fwd.index) {
      // No index → BE can't address this row. Surface the same typed
      // error wording the BE would emit if we sent an empty body.
      setDeleteError(translatePortForwardError(t, 'missing_field', null));
      return;
    }
    setDeleting(key);
    setDeleteError(null);
    try {
      const resp = await deleteRouterForward(zoneId, { index: fwd.index });
      const body = resp.data as RouterForwardDeleteResponse;
      if (body.ok) {
        setConfirmTarget(null);
        onChanged();
      } else {
        setDeleteError(translatePortForwardError(t, body.error, body.detail));
      }
    } catch (err) {
      setDeleteError(
        extractApiError(
          isAxiosError(err) ? err : undefined,
          err instanceof Error ? err.message : t('error.general'),
        ),
      );
    } finally {
      setDeleting(null);
    }
  };

  return (
    <section className="space-y-2.5" data-testid="port-forwards-table">
      {/* The wrapping <CollapsibleSection> in RouterPanel.tsx renders
          the section title now; this row keeps only the Add CTA so the
          table-vs-title hierarchy doesn't double up. */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="size-3.5" />
          {t('equipment.router.forwards.add')}
        </Button>
      </div>

      {forwards.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
          {t('equipment.router.forwards.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card/40">
          {/* Header row — desktop only; on mobile the rows stack and
              the label is read from the row's own values. */}
          <div className="hidden grid-cols-[8rem_3.5rem_1fr_minmax(8rem,1fr)_minmax(0,2fr)_2.25rem] gap-x-3 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground sm:grid">
            <span>{t('equipment.router.forwards.field.interface')}</span>
            <span>{t('equipment.router.forwards.field.protocol')}</span>
            <span>{t('equipment.router.forwards.field.externalPort')} → {t('equipment.router.forwards.field.internalPort')}</span>
            <span>{t('equipment.router.forwards.field.toHost')}</span>
            <span>{t('equipment.router.forwards.field.comment')}</span>
            <span className="sr-only">{t('equipment.router.forwards.delete')}</span>
          </div>
          <ul className="divide-y divide-border">
            {forwards.map(fwd => {
              const k = keyOf(fwd);
              const isDeleting = deleting === k;
              return (
                <li
                  key={k}
                  className="group grid grid-cols-2 items-center gap-x-3 gap-y-1 px-3 py-2 text-xs transition-colors hover:bg-muted/40 sm:grid-cols-[8rem_3.5rem_1fr_minmax(8rem,1fr)_minmax(0,2fr)_2.25rem]"
                  data-testid="port-forward-row"
                >
                  <span className="inline-flex w-fit items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    {fwd.interface || '?'}
                  </span>
                  <span className="inline-flex w-fit items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    {(fwd.protocol || 'tcp').toUpperCase()}
                  </span>
                  <span className="font-mono tabular-nums">
                    {fwd.external_port ?? '?'}
                    <span className="mx-1 text-muted-foreground/60">→</span>
                    {fwd.internal_port ?? '?'}
                  </span>
                  <span className="truncate font-mono tabular-nums text-foreground/90">
                    {fwd.to_host || '—'}
                  </span>
                  <Tooltip content={fwd.comment || undefined}><span
                    className={cn(
                      'col-span-2 truncate text-muted-foreground sm:col-span-1',
                      !fwd.comment && 'text-muted-foreground/50',
                    )}
                  >
                    {fwd.comment || '—'}
                  </span></Tooltip>
                  <div className="col-start-2 row-start-1 flex justify-end sm:col-auto sm:row-auto">
                    <button
                      type="button"
                      className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => { setDeleteError(null); setConfirmTarget(fwd); }}
                      disabled={isDeleting}
                      aria-label={t('equipment.router.forwards.delete')}
                      data-testid="port-forward-delete"
                    >
                      {isDeleting ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="size-3.5" />
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <AddPortForwardDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        zoneId={zoneId}
        lanHosts={lanHosts}
        defaultInterface={defaultInterface}
        onCreated={onChanged}
      />

      <Dialog open={!!confirmTarget} onOpenChange={v => !v && setConfirmTarget(null)}>
        <DialogContent className="relative">
          <DialogHeader>
            <DialogTitle>{t('equipment.router.forwards.deleteDialog.title')}</DialogTitle>
          </DialogHeader>
          {confirmTarget && (
            <div className="space-y-3 text-sm">
              <p className="leading-relaxed text-muted-foreground">
                {t('equipment.router.forwards.deleteDialog.body')}
              </p>
              <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 font-mono text-xs tabular-nums">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    {confirmTarget.interface || '?'}
                  </span>
                  <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    {(confirmTarget.protocol || 'tcp').toUpperCase()}
                  </span>
                  <span>
                    {confirmTarget.external_port}
                    <span className="mx-1 text-muted-foreground/60">→</span>
                    {confirmTarget.internal_port}
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span>{confirmTarget.to_host}</span>
                </div>
              </div>
              {deleteError && (
                <div className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {deleteError}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmTarget(null)}
              disabled={!!deleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={onDeleteConfirm}
              disabled={!!deleting}
              data-testid="port-forward-confirm-delete"
            >
              {deleting && <Loader2 className="size-3.5 animate-spin" />}
              {t('equipment.router.forwards.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
